import { ReactNode } from 'react';
import { Box, Container, Grid, Typography, alpha } from '@mui/material';
import { motion } from 'framer-motion';
import { TeamOutlined, SmileOutlined, HomeOutlined } from '@ant-design/icons';

// ==============================|| SERVICE LIST SECTION ||============================== //
//
// 料金の直前に置く、対応サービスの一覧。
// 機能を見て「うちの事業所で使えるのか」を確かめてから料金を見る流れにしたいため、
// 機能紹介と料金の間に挟んでいる。児童福祉法に基づく3サービスが対象。

const primaryColor = '#2aa1af';

type Service = {
    icon: ReactNode;
    name: string;
};

const services: Service[] = [
    { icon: <TeamOutlined />, name: '放課後等デイサービス' },
    { icon: <SmileOutlined />, name: '児童発達支援' },
    { icon: <HomeOutlined />, name: '保育所等訪問支援' }
];

const ServiceListSection = () => (
    // 前後が白(FeatureOverviewSection)と淡いグレー(PricingSection)のため、
    // どちらとも見分けられるようブランドカラーの淡色を敷いている。
    <Box
        component="section"
        id="service"
        sx={{ py: { xs: 8, md: 11 }, bgcolor: alpha(primaryColor, 0.12), overflow: 'hidden' }}
    >
        <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                >
                    <Typography
                        variant="caption"
                        component="span"
                        sx={{ color: primaryColor, fontWeight: 900, letterSpacing: '0.2em', display: 'block', mb: 2 }}
                    >
                        SERVICE
                    </Typography>
                    <Typography
                        variant="h2"
                        component="h2"
                        sx={{ fontWeight: 900, fontSize: { xs: '1.9rem', md: '2.6rem' }, color: '#1a1a1a', lineHeight: 1.4 }}
                    >
                        対応サービス一覧
                    </Typography>
                    <Box aria-hidden="true" sx={{ width: 56, height: 5, borderRadius: 3, bgcolor: primaryColor, mx: 'auto', mt: 2.5 }} />
                </motion.div>
            </Box>

            <Grid container spacing={3} alignItems="stretch">
                {services.map((service, index) => (
                    <Grid item xs={12} md={4} key={service.name}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                            style={{ height: '100%' }}
                        >
                            <Box
                                component="article"
                                sx={{
                                    height: '100%',
                                    px: { xs: 2.5, md: 3 },
                                    py: { xs: 2.5, md: 3 },
                                    borderRadius: '18px',
                                    bgcolor: '#fff',
                                    border: `1px solid ${alpha(primaryColor, 0.15)}`,
                                    boxShadow: `0 10px 28px ${alpha(primaryColor, 0.05)}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: { xs: 'flex-start', md: 'center' },
                                    gap: 2
                                }}
                            >
                                <Box
                                    aria-hidden="true"
                                    sx={{
                                        flexShrink: 0,
                                        width: 46,
                                        height: 46,
                                        borderRadius: '14px',
                                        bgcolor: alpha(primaryColor, 0.09),
                                        color: primaryColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '1.25rem'
                                    }}
                                >
                                    {service.icon}
                                </Box>
                                <Typography
                                    variant="h3"
                                    component="h3"
                                    sx={{ fontWeight: 900, fontSize: { xs: '1.02rem', md: '1.08rem' }, color: '#1a1a1a', lineHeight: 1.5 }}
                                >
                                    {service.name}
                                </Typography>
                            </Box>
                        </motion.div>
                    </Grid>
                ))}
            </Grid>
        </Container>
    </Box>
);

export default ServiceListSection;
